import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import LenderLogo from './LenderLogo';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  ArrowLeft, Check, CheckCircle2, Circle, CircleDot, FileDown, FileText,
  Loader2, Save, RotateCcw, AlertCircle, Landmark, Send, History, Timer,
  Zap, ChevronDown, ChevronUp, Settings, ScrollText,
  PenLine, ClipboardCheck, ShieldCheck, MessageSquare, FileSignature, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { generateLenderPdf, generateInternalPdf, setPdfBrand } from './lenderPackPdfExport';
import { useBranding } from '../../hooks/useBranding';
import MIRChecklist from './MIRChecklist';
import LodgeConfirmDialog from './LodgeConfirmDialog';
import PostLodgementCommandCentre, { ApplicationTimeline } from './PostLodgementCommandCentre';
import { ZoneHeader, ZONE_STYLE } from './ZoneHeader';
import InternalCommentsThread from './InternalCommentsThread';
import CommentsDrawer from './CommentsDrawer';
import LiabilitiesSection from './LiabilitiesSection';
import AuditLogDialog from './AuditLogDialog';
import JourneySections from './JourneySections';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const STATUS_LABELS = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  waiting_on_info: 'Waiting on Info',
  ready_for_review: 'Ready for Review',
  changes_required: 'Changes Required',
  ready_to_submit: 'Ready to Submit',
  lodged: 'Lodged with Lender',
  mir_outstanding: 'MIR Outstanding',
  waiting_on_client: 'Waiting on Client',
  waiting_on_derwent: 'Waiting on Derwent',
  waiting_on_third_party: 'Waiting on Third Party',
  all_mirs_satisfied: 'All MIRs Satisfied · Waiting on Lender',
  back_with_lender: 'Back with Lender',
  conditional_approval: 'Conditional Approval',
  formal_approval: 'Formal Approval',
  completed: 'Completed',
};

const SLA_META = [
  ['initial_assessment',   'Initial assessment'],
  ['mir_assessment',       'MIR assessment'],
  ['conditional_approval', 'Conditional approval'],
  ['formal_approval',      'Formal approval'],
  ['loan_documents',       'Loan documents'],
  ['settlement_booking',   'Settlement booking'],
];

const STATUS_TONES = {
  not_started: 'bg-slate-100 text-slate-700 border-slate-300',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
  waiting_on_info: 'bg-amber-100 text-amber-800 border-amber-300',
  ready_for_review: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  changes_required: 'bg-rose-100 text-rose-800 border-rose-300',
  ready_to_submit: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  lodged: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  mir_outstanding: 'bg-amber-100 text-amber-800 border-amber-300',
  waiting_on_client: 'bg-amber-100 text-amber-800 border-amber-300',
  waiting_on_derwent: 'bg-blue-100 text-blue-800 border-blue-300',
  waiting_on_third_party: 'bg-purple-100 text-purple-800 border-purple-300',
  all_mirs_satisfied: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  back_with_lender: 'bg-teal-100 text-teal-800 border-teal-300',
  conditional_approval: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  formal_approval: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  completed: 'bg-slate-200 text-slate-800 border-slate-400',
};

// Inline HTML-safe label — the template stores <b> tags for emphasis.
function LabelInline({ html, className }) {
  return <span className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}

export default function PackBuilder({ packId, onBack, onPackChange }) {
  const { token } = useAuth();
  const brand = useBranding();
  useEffect(() => { setPdfBrand(brand); }, [brand]);
  const [pack, setPack] = useState(null);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  // Pending changes buffer for autosave
  const pendingRef = useRef({});
  const timerRef = useRef(null);
  const [showLodgeDialog, setShowLodgeDialog] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [showPackDetails, setShowPackDetails] = useState(false);  // collapse pre-lodgement editor on post-lodgement view
  const [showReopenConfirm, setShowReopenConfirm] = useState(false);

  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/lender-packs/${packId}`, { headers: auth });
      if (!res.ok) throw new Error('Failed to load pack');
      const d = await res.json();
      setPack(d.pack);
      setTemplate(d.template);
    } catch (e) {
      toast.error(`Failed to load — ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [packId, auth]);

  useEffect(() => { load(); }, [load]);

  // ── Autosave ────────────────────────────────────────────
  const flush = useCallback(async () => {
    if (Object.keys(pendingRef.current).length === 0) return;
    const body = { ...pendingRef.current };
    pendingRef.current = {};
    setSaving(true);
    try {
      const res = await fetch(`${backendUrl}/api/lender-packs/${packId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      const updated = await res.json();
      setPack(updated);
      setSavedAt(new Date());
      onPackChange?.(updated);
    } catch (e) {
      toast.error(`Autosave failed — ${e.message}`);
      // Restore pending so next attempt retries
      pendingRef.current = { ...body, ...pendingRef.current };
    } finally {
      setSaving(false);
    }
  }, [packId, auth, onPackChange]);

  const queueChange = useCallback((patch) => {
    // merge answers / na_groups / scalar updates
    pendingRef.current = { ...pendingRef.current };
    if (patch.answers) {
      pendingRef.current.answers = { ...(pendingRef.current.answers || {}), ...patch.answers };
    }
    if (patch.na_groups) {
      pendingRef.current.na_groups = { ...(pendingRef.current.na_groups || {}), ...patch.na_groups };
    }
    if (patch.status !== undefined) pendingRef.current.status = patch.status;
    if (patch.credit_team_member !== undefined) pendingRef.current.credit_team_member = patch.credit_team_member;
    if (patch.application_number !== undefined) pendingRef.current.application_number = patch.application_number;
    if (patch.deal_number !== undefined) pendingRef.current.deal_number = patch.deal_number;
    if (patch.sla) pendingRef.current.sla = { ...(pendingRef.current.sla || {}), ...patch.sla };

    // Optimistic UI update
    setPack(prev => prev && ({
      ...prev,
      ...(patch.status !== undefined ? { status: patch.status } : {}),
      ...(patch.credit_team_member !== undefined ? { credit_team_member: patch.credit_team_member } : {}),
      ...(patch.application_number !== undefined ? { application_number: patch.application_number } : {}),
      ...(patch.deal_number !== undefined ? { deal_number: patch.deal_number } : {}),
      answers: patch.answers ? { ...(prev.answers || {}), ...patch.answers } : prev.answers,
      na_groups: patch.na_groups ? { ...(prev.na_groups || {}), ...patch.na_groups } : prev.na_groups,
      sla: patch.sla ? { ...(prev.sla || {}), ...patch.sla } : prev.sla,
    }));

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, 800);
  }, [flush]);

  // Flush on unmount / tab hide so no keystroke is lost
  useEffect(() => {
    const onHide = () => flush();
    window.addEventListener('beforeunload', onHide);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      onHide();
      window.removeEventListener('beforeunload', onHide);
      document.removeEventListener('visibilitychange', onHide);
    };
  }, [flush]);

  // ── Answer helpers ──────────────────────────────────────
  const val = (fid) => (pack?.answers || {})[fid] ?? '';
  const setVal = (fid, v) => queueChange({ answers: { [fid]: v } });
  const setNa = (gid, v) => queueChange({ na_groups: { [gid]: v } });

  // ── PDF exports ─────────────────────────────────────────
  const exportLender = async () => {
    if (!pack || !template) return;
    try { await generateLenderPdf(pack, template); }
    catch (e) { toast.error(`Lender PDF failed — ${e.message}`); }
  };
  const exportInternal = async () => {
    if (!pack || !template) return;
    try { await generateInternalPdf(pack, template); }
    catch (e) { toast.error(`Internal PDF failed — ${e.message}`); }
  };

  const setSla = (key, val) => queueChange({ sla: { [key]: Math.max(0, Number(val) || 0) } });

  const doLodge = async (lodgedIso) => {
    if (pack.completion < 100) {
      toast.error(`Cannot lodge — ${pack.outstanding.length} items still outstanding`);
      return;
    }
    await flush();
    try {
      const res = await fetch(`${backendUrl}/api/lender-packs/${packId}/lodge`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify({ confirmed: true, lodged_at: lodgedIso }),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success('Application lodged with lender · SLA countdown started');
      setShowLodgeDialog(false);
      await load();
      onPackChange?.();
    } catch (e) { toast.error(`Lodge failed — ${e.message}`); }
  };

  const completeNow = async () => {
    await flush();
    try {
      const res = await fetch(`${backendUrl}/api/lender-packs/${packId}/complete`, {
        method: 'POST', headers: auth,
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success('Pack marked completed');
      await load();
      onPackChange?.();
    } catch (e) { toast.error(`Complete failed — ${e.message}`); }
  };

  const reopenNow = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/lender-packs/${packId}/reopen`, {
        method: 'POST', headers: auth,
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success('Pack reopened');
      await load();
      onPackChange?.();
    } catch (e) { toast.error(`Reopen failed — ${e.message}`); }
  };

  if (loading) {
    return <div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading pack…</div>;
  }
  if (!pack || !template) return <div className="p-8 text-slate-500">Pack not found.</div>;

  const isDone = pack.status === 'completed';
  const savedLabel = saving ? 'Saving…'
    : savedAt ? `Saved · ${savedAt.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}`
    : `Last saved · ${new Date(pack.last_updated_at).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}`;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sticky toolbar */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm" data-testid="pack-builder-toolbar">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-4">
          <button onClick={onBack} className="flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-slate-900" data-testid="pack-builder-back">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </button>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <LenderLogo lender={pack.lender} size="md" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-black text-slate-900 truncate">{pack.client_name}</div>
              <div className="text-[11px] text-slate-500 truncate">
                Broker {pack.broker}
                {pack.credit_team_member ? ` · Credit ${pack.credit_team_member}` : ''}
              </div>
            </div>
          </div>
          <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${STATUS_TONES[pack.status]}`}>
            {STATUS_LABELS[pack.status]}
          </div>
          <div className="hidden md:flex items-center gap-1 text-[11px] font-medium text-slate-500" data-testid="pack-autosave-indicator">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 text-emerald-600" />}
            {savedLabel}
          </div>
          <Button onClick={() => setShowAudit(true)} variant="outline" size="sm" className="text-xs" data-testid="pack-audit-log">
            <ScrollText className="w-3 h-3 mr-1" /> Audit Log
          </Button>
          <Button onClick={exportLender} variant="outline" size="sm" className="text-xs" data-testid="pack-pdf-lender">
            <FileText className="w-3 h-3 mr-1" /> Lender PDF
          </Button>
          <Button onClick={exportInternal} variant="outline" size="sm" className="text-xs" data-testid="pack-pdf-internal">
            <FileDown className="w-3 h-3 mr-1" /> Internal PDF
          </Button>
          {pack.phase === 'pre_lodgement' ? (
            <Button onClick={() => setShowLodgeDialog(true)} size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
              data-testid="pack-lodge"
              disabled={pack.completion < 100}>
              <Send className="w-3 h-3 mr-1" /> Lodge with Lender
            </Button>
          ) : pack.phase === 'post_lodgement' ? (
            <>
              <Button onClick={() => setShowReopenConfirm(true)} variant="outline" size="sm" className="text-xs" data-testid="pack-reopen">
                <RotateCcw className="w-3 h-3 mr-1" /> Reopen
              </Button>
              <Button onClick={completeNow} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid="pack-complete">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Complete file
              </Button>
            </>
          ) : (
            <Button onClick={() => setShowReopenConfirm(true)} size="sm" className="bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid="pack-reopen">
              <RotateCcw className="w-3 h-3 mr-1" /> Reopen
            </Button>
          )}
        </div>
      </div>

      {/* Main body */}
      <div className="max-w-6xl mx-auto px-6 py-4 grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-5">
        {/* Form */}
        <div className="space-y-4">
          {/* ── Journey Sections (Phase 3/4) — front-of-funnel + settlement
              editors, visible for every stage EXCEPT lodged (which has
              its own Post-Lodgement command centre).                    */}
          {['lead', 'discovery', 'portal', 'credit', 'proposal', 'post_lodgement', 'settlement'].includes(pack.stage) && (
            <JourneySections
              pack={pack}
              onPackChange={(updated) => { setPack(updated); onPackChange?.(updated); }}
            />
          )}

          {/* ── Post-Lodgement Command Centre (appears above everything once lodged) ── */}
          {pack.phase === 'post_lodgement' && (
            <>
              <PostLodgementCommandCentre
                pack={pack}
                onUpdate={(patch) => queueChange(patch)}
                onFocusMirs={() => {
                  const el = document.getElementById('mir-section');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              />
              <MIRChecklist pack={pack} step={3} onPackChange={(updated) => { setPack(updated); onPackChange?.(updated); }} />
              <ApplicationTimeline pack={pack} step={4} />
            </>
          )}

          {/* ── Legacy pack-editor collapsible on post-lodgement, visible by default pre-lodgement ── */}
          {pack.phase === 'post_lodgement' ? (
            <div className="rounded-2xl border-2 shadow-sm overflow-hidden bg-white" style={{ borderColor: ZONE_STYLE.packContents.border, colorScheme: 'light' }}>
              <ZoneHeader
                step={5}
                icon={Settings}
                title="Pack Contents"
                subtitle="Deal summary, SLA config, broker notes, checklists — tweak after lodgement if required."
                {...ZONE_STYLE.packContents}
                testid="pack-details-toggle"
                collapsible
                collapsed={!showPackDetails}
                onToggle={() => setShowPackDetails(v => !v)}
                right={
                  <span className="text-[9.5px] font-black uppercase tracking-widest px-2 py-1 rounded-full" style={{ background: ZONE_STYLE.packContents.accent, color: '#ffffff' }}>
                    {showPackDetails ? 'Expanded' : 'Collapsed'}
                  </span>
                }
              />
              {showPackDetails && (
                <div className="p-4 space-y-6" style={{ background: '#f8fafc' }}>
                  <LegacyPackEditor
                    pack={pack} template={template} queueChange={queueChange} val={val} setVal={setVal} setNa={setNa}
                    setSla={setSla} showActivity={showActivity} setShowActivity={setShowActivity} isDone={isDone}
                    onPackChange={(updated) => { setPack(updated); onPackChange?.(updated); }}
                  />
                </div>
              )}
            </div>
          ) : (
            <LegacyPackEditor
              pack={pack} template={template} queueChange={queueChange} val={val} setVal={setVal} setNa={setNa}
              setSla={setSla} showActivity={showActivity} setShowActivity={setShowActivity} isDone={isDone}
              onPackChange={(updated) => { setPack(updated); onPackChange?.(updated); }}
            />
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-3 lg:sticky lg:top-24 lg:self-start">
          <div className="bg-white rounded-lg border border-slate-200 p-3 shadow-sm" data-testid="pack-completion-panel">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[9.5px] font-black uppercase tracking-wider text-slate-500">Completion</span>
              <span className="text-base font-black text-slate-900 tabular-nums">{pack.completion}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mb-2">
              <div className="h-full bg-emerald-500 transition-all"
                style={{ width: `${pack.completion || 0}%` }} />
            </div>
            <div className="text-[10.5px] text-slate-500">
              {pack.completion === 100 ? 'All mandatory items complete.'
                : `${(pack.outstanding || []).length} outstanding item${pack.outstanding?.length === 1 ? '' : 's'}`}
            </div>
          </div>

          {(pack.outstanding || []).length > 0 && (
            <div className="bg-white rounded-lg border border-slate-200 p-3 shadow-sm" data-testid="pack-outstanding-list">
              <div className="flex items-center gap-1.5 mb-1.5">
                <AlertCircle className="w-3 h-3 text-amber-600" />
                <span className="text-[9.5px] font-black uppercase tracking-wider text-slate-500">Outstanding items</span>
              </div>
              <ul className="space-y-1 max-h-[380px] overflow-y-auto pr-1">
                {pack.outstanding.map((o, idx) => (
                  <li key={idx} className="text-[10.5px] text-slate-700 leading-snug flex items-start gap-1.5" data-testid={`outstanding-${idx}`}>
                    <Circle className="w-2.5 h-2.5 mt-0.5 shrink-0 text-slate-400" />
                    <span className="truncate" title={o.label}>{o.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Lodge confirmation modal */}
      <LodgeConfirmDialog
        open={showLodgeDialog}
        onClose={() => setShowLodgeDialog(false)}
        onConfirm={doLodge}
        pack={pack}
      />

      {/* Reopen confirmation dialog */}
      <AlertDialog open={showReopenConfirm} onOpenChange={setShowReopenConfirm}>
        <AlertDialogContent data-testid="reopen-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reopen this application?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reopen and continue working on this application?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reopen-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => { setShowReopenConfirm(false); await reopenNow(); }}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="reopen-confirm"
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Persistent internal comments — floating toggle + slide-out drawer,
          accessible from every section of the pack builder. */}
      <CommentsDrawer
        pack={pack}
        onPackChange={(updated) => { setPack(updated); onPackChange?.(updated); }}
      />

      {/* Audit log — full history of every action on this client's pack. */}
      <AuditLogDialog
        open={showAudit}
        onClose={() => setShowAudit(false)}
        pack={pack}
      />
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Section renderer
// ─────────────────────────────────────────────────────────────────────
// ─── Section styling map (per section.id) — icons + step + subtitle
// Palette is shared with post-lodgement zones via ZONE_STYLE so both pre and
// post lodgement sections look identical.
const SECTION_META = {
  notes:             { step: 1, icon: PenLine,        subtitle: 'Credit team to write the story of the deal — these 8 notes flow directly into the lender PDF.' },
  documents:         { step: 2, icon: FileText,       subtitle: 'Tick every document that is included in the submission package.' },
  checklist:         { step: 3, icon: ClipboardCheck, subtitle: 'Confirm every mandatory policy item the lender expects to see.' },
  qc:                { step: 4, icon: ShieldCheck,    subtitle: 'Internal quality control — does not appear in the lender PDF.' },
  internal_comments: { step: 5, icon: MessageSquare,  subtitle: 'Risks, exceptions and recommendations — internal broker + credit report only.' },
  signoff:           { step: 6, icon: FileSignature,  subtitle: 'Broker sign-off — the final step before this file is ready to lodge.' },
};

// Compute how many mandatory items are complete inside a section
function computeSectionProgress(section, pack) {
  const answers = pack.answers || {};
  const naGroups = pack.na_groups || {};
  if (section.id === 'internal_comments') {
    const count = (pack.internal_comments || []).length;
    return { done: count, total: null, label: 'comments', isCount: true };
  }
  if (section.kind === 'notes' || section.kind === 'signoff') {
    const mand = section.fields.filter(f => f.mandatory);
    const done = mand.filter(f => String(answers[f.id] || '').trim()).length;
    return { done, total: mand.length, label: section.kind === 'signoff' ? 'signed' : 'written' };
  }
  if (section.kind === 'docs') {
    const boxes = section.fields.filter(f => f.type === 'checkbox');
    const done = boxes.filter(f => !!answers[f.id]).length;
    return { done, total: boxes.length, label: 'ticked' };
  }
  if (section.kind === 'checklist') {
    let done = 0, total = 0;
    section.groups.forEach(g => {
      if (g.na && naGroups[g.id]) return;
      if (g.any_of) {
        total += 1;
        if (g.fields.some(f => !!answers[f.id])) done += 1;
      } else {
        const mand = g.fields.filter(f => f.mandatory);
        total += mand.length;
        done  += mand.filter(f => !!answers[f.id]).length;
      }
    });
    return { done, total, label: 'confirmed' };
  }
  return { done: 0, total: 0, label: '' };
}

function Section({ section, pack, val, setVal, setNa, onPackChange }) {
  const answers = pack.answers || {};
  const answerMeta = pack.answer_meta || {};
  const naGroups = pack.na_groups || {};

  const meta = SECTION_META[section.id] || SECTION_META.notes;
  const zoneStyle = ZONE_STYLE[section.id] || ZONE_STYLE.notes;
  const progress = computeSectionProgress(section, pack);
  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
  const complete = progress.total > 0 && progress.done === progress.total;
  // Internal comments starts collapsed so it doesn't dominate the layout.
  // Sections that are already 100% complete on mount also start collapsed
  // to keep the page clean.
  const [collapsed, setCollapsed] = useState(
    section.id === 'internal_comments' || complete
  );
  // Auto-collapse when a section transitions from incomplete → complete so
  // the broker instantly sees the page tidy up once they've finished a step.
  const wasComplete = useRef(complete);
  useEffect(() => {
    if (complete && !wasComplete.current) setCollapsed(true);
    wasComplete.current = complete;
  }, [complete]);

  const rightBadge = progress.isCount ? (
    <span className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full"
      style={{ background: zoneStyle.accent, color: '#ffffff' }}>
      {progress.done} {progress.label}
    </span>
  ) : progress.total > 0 ? (
    <div className="text-right">
      <div className="text-[9.5px] font-black uppercase tracking-widest" style={{ color: zoneStyle.mutedInk }}>{progress.label}</div>
      <div className="flex items-center gap-2 mt-0.5">
        <div className="w-24 h-1.5 rounded-full overflow-hidden" style={{ background: '#e2e8f0' }}>
          <div className="h-full transition-all" style={{ width: `${pct}%`, background: complete ? '#10b981' : zoneStyle.accent }} />
        </div>
        <span className="text-xs font-black tabular-nums" style={{ color: zoneStyle.ink }}>
          {progress.done}<span className="opacity-60">/{progress.total}</span>
          {complete && <Check className="w-3 h-3 inline ml-1" style={{ color: '#10b981' }} />}
        </span>
      </div>
    </div>
  ) : null;

  return (
    <div className="rounded-2xl border shadow-sm overflow-hidden bg-white"
      style={{ borderColor: '#e5e7eb', colorScheme: 'light' }}
      data-testid={`pack-section-${section.id}`}>
      <ZoneHeader
        step={meta.step}
        icon={meta.icon}
        title={section.title}
        subtitle={meta.subtitle}
        {...zoneStyle}
        internal={!!zoneStyle.internal}
        testid={`section-toggle-${section.id}`}
        collapsible
        collapsed={collapsed}
        onToggle={() => setCollapsed(v => !v)}
        right={rightBadge}
      />

      {/* Body */}
      {!collapsed && (
        <div className="p-5 sm:p-6" style={{ background: '#ffffff' }}>
          {section.id === 'internal_comments' ? (
            <InternalCommentsThread pack={pack} onPackChange={onPackChange} />
          ) : section.kind === 'notes' ? (
            <div className="space-y-5">
              {section.fields.map((f, idx) => (
                <NoteFieldRow key={f.id} field={f} value={val(f.id)}
                  onChange={(v) => setVal(f.id, v)}
                  meta={answerMeta[f.id]}
                  number={idx + 1}
                  accent={zoneStyle.accent}
                  border={zoneStyle.border}
                  ink={zoneStyle.ink}
                  mutedInk={zoneStyle.mutedInk} />
              ))}
            </div>
          ) : section.kind === 'signoff' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {section.fields.map(f => (
                <FieldRow key={f.id} field={f} value={val(f.id)}
                  onChange={(v) => setVal(f.id, v)}
                  meta={answerMeta[f.id]} />
              ))}
            </div>
          ) : section.kind === 'docs' ? (
            <DocsGrid section={section} answers={answers} answerMeta={answerMeta}
              setVal={setVal} val={val} accent={zoneStyle.accent} border={zoneStyle.border} />
          ) : section.kind === 'checklist' ? (
            <div className="space-y-3">
              {section.groups.map(group => (
                <ChecklistGroup key={group.id} group={group} sectionId={section.id}
                  naOn={!!naGroups[group.id]} onToggleNa={(v) => setNa(group.id, v)}
                  answers={answers} answerMeta={answerMeta} setVal={setVal}
                  liabilities={pack.liabilities || []}
                  accent={zoneStyle.accent} border={zoneStyle.border} />
              ))}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

// ─── Broker notes row — numbered card with lender guidance ────
function NoteFieldRow({ field, value, onChange, meta, number, accent, border, ink, mutedInk }) {
  const testid = `pack-field-${field.id}`;
  const hasContent = String(value || '').trim().length > 0;
  // Auto-collapse once the note has content. Click header (or the preview line)
  // to expand and edit; auto-collapses again on blur when content is present.
  const [expanded, setExpanded] = useState(!hasContent);

  const preview = String(value || '').replace(/\s+/g, ' ').trim().slice(0, 140);
  return (
    <div className="rounded-xl border overflow-hidden" data-testid={`note-card-${field.id}`}
      style={{ borderColor: hasContent ? border : '#e2e8f0', background: '#ffffff' }}>
      {/* Numbered header — clickable when collapsed. */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left"
        data-testid={`note-toggle-${field.id}`}
      >
        <div className="flex items-center justify-between px-3 py-2 border-b hover:bg-slate-50 transition"
          style={{ background: hasContent ? '#f8fafc' : '#fefefe', borderColor: hasContent ? border : '#f1f5f9' }}>
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
              style={{ background: hasContent ? accent : '#cbd5e1' }}>
              <span className="text-[11px] font-black tabular-nums" style={{ color: '#ffffff' }}>{number}</span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-black leading-tight" style={{ color: ink }}>
                {field.label}
                {field.mandatory && <span className="ml-1" style={{ color: '#e11d48' }}>*</span>}
              </div>
              {meta?.updated_at && hasContent && (
                <div className="text-[9.5px] font-semibold mt-0.5" style={{ color: mutedInk }} data-testid={`meta-${field.id}`}>
                  {formatMetaShort(meta)}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {hasContent
              ? <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: '#dcfce7', color: '#166534' }}><Check className="w-2.5 h-2.5" />Written</span>
              : (field.mandatory && <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: '#fee2e2', color: '#991b1b' }}>Required</span>)}
            {expanded
              ? <ChevronUp className="w-3.5 h-3.5" style={{ color: mutedInk }} />
              : <ChevronDown className="w-3.5 h-3.5" style={{ color: mutedInk }} />}
          </div>
        </div>
      </button>

      {/* Collapsed preview — one-line teaser of the current answer. */}
      {!expanded && hasContent && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="w-full text-left px-3 py-2 hover:bg-slate-50 transition"
          data-testid={`note-preview-${field.id}`}
        >
          <p className="text-[12px] leading-relaxed line-clamp-2" style={{ color: '#334155' }}>
            {preview}{String(value || '').length > 140 ? '…' : ''}
          </p>
          <span className="text-[10px] font-semibold text-indigo-700 hover:text-indigo-900">
            Edit note
          </span>
        </button>
      )}

      {/* Expanded body — guidance + textarea. */}
      {expanded && (
        <>
          {(field.lender_instruction || field.internal_instruction) && (
            <div className="px-3 pt-2.5 space-y-1.5">
              {field.lender_instruction && (
                <div className="rounded-md px-2.5 py-1.5" style={{ background: '#fef3c7', border: '1px solid #fcd34d' }}>
                  <div className="text-[9px] font-black uppercase tracking-wider" style={{ color: '#92400e' }}>Lender guidance</div>
                  <LabelInline html={field.lender_instruction} className="text-[11px] leading-snug block" style={{ color: '#78350f' }} />
                </div>
              )}
              {field.internal_instruction && (
                <div className="rounded-md px-2.5 py-1.5" style={{ background: '#f1f5f9', border: '1px solid #e2e8f0' }}>
                  <div className="text-[9px] font-black uppercase tracking-wider" style={{ color: '#475569' }}>Broker prompt</div>
                  <p className="text-[11px] leading-snug" style={{ color: '#334155' }}>{field.internal_instruction}</p>
                </div>
              )}
            </div>
          )}
          <div className="p-3">
            <Textarea value={value || ''} onChange={e => onChange(e.target.value)}
              onBlur={() => { if (String(value || '').trim()) setExpanded(false); }}
              rows={field.rows || 4} data-testid={testid}
              autoFocus={hasContent}
              className="text-sm leading-relaxed" style={{ color: '#0f172a' }}
              placeholder="Type your note…" />
          </div>
        </>
      )}
    </div>
  );
}

// ─── Documents grid with a clean two-column card layout ───────
function DocsGrid({ section, answers, answerMeta, setVal, val, accent, border }) {
  const checkboxes = section.fields.filter(f => f.type === 'checkbox');
  const textFields = section.fields.filter(f => f.type === 'text');
  const tickedCount = checkboxes.filter(f => !!answers[f.id]).length;
  return (
    <div className="space-y-4">
      {tickedCount > 0 && (
        <div className="text-[11px] font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5" style={{ background: '#dcfce7', color: '#166534' }}>
          <Check className="w-3 h-3" /> <span>{tickedCount} document{tickedCount === 1 ? '' : 's'} ticked so far</span>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {checkboxes.map(f => (
          <CheckboxRow key={f.id} field={f} value={!!answers[f.id]}
            onChange={(v) => setVal(f.id, v)}
            meta={answerMeta[f.id]}
            accent={accent} border={border} />
        ))}
      </div>
      {textFields.length > 0 && (
        <div className="pt-3 border-t space-y-2" style={{ borderColor: '#e2e8f0' }}>
          {textFields.map(f => (
            <div key={f.id}>
              <label className="text-[11px] font-black uppercase tracking-widest mb-1 block" style={{ color: '#475569' }}>{f.label}</label>
              <Input value={val(f.id) || ''} onChange={e => setVal(f.id, e.target.value)}
                data-testid={`pack-field-${f.id}`} placeholder="e.g. any additional documents supplied…"
                style={{ color: '#0f172a' }} />
              {answerMeta[f.id]?.updated_at && val(f.id) && (
                <div className="text-[9.5px] mt-0.5" style={{ color: '#94a3b8' }}>{formatMetaShort(answerMeta[f.id])}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function FieldRow({ field, value, onChange, meta }) {
  const testid = `pack-field-${field.id}`;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <label className="text-xs font-semibold text-slate-800">
          {field.label} {field.mandatory && <span className="text-rose-500">*</span>}
        </label>
        {meta?.updated_at && (
          <span className="text-[9.5px] text-slate-400" data-testid={`meta-${field.id}`}>
            {formatMetaShort(meta)}
          </span>
        )}
      </div>
      {field.lender_instruction ? (
        <div className="mb-1.5 rounded-md bg-amber-50 border border-amber-200 px-2.5 py-1.5">
          <div className="text-[9px] font-black uppercase tracking-wider text-amber-800 mb-0.5">Lender guidance</div>
          <LabelInline html={field.lender_instruction} className="text-[11px] text-amber-900 leading-snug block" />
        </div>
      ) : null}
      {field.internal_instruction ? (
        <div className="mb-1.5 rounded-md bg-slate-50 border border-slate-200 px-2.5 py-1.5">
          <div className="text-[9px] font-black uppercase tracking-wider text-slate-600 mb-0.5">Broker prompt</div>
          <p className="text-[11px] text-slate-700 leading-snug">{field.internal_instruction}</p>
        </div>
      ) : null}
      {field.type === 'textarea' ? (
        <Textarea value={value || ''} onChange={e => onChange(e.target.value)}
          rows={field.rows || 3} data-testid={testid} className="text-sm" />
      ) : field.type === 'date' ? (
        <Input type="date" value={value || ''} onChange={e => onChange(e.target.value)} data-testid={testid} />
      ) : (
        <Input value={value || ''} onChange={e => onChange(e.target.value)} data-testid={testid} />
      )}
    </div>
  );
}


function CheckboxRow({ field, value, onChange, meta, accent = '#2563eb', border = '#e2e8f0' }) {
  return (
    <label
      className="flex items-start gap-2 px-3 py-2.5 rounded-lg border cursor-pointer transition-all hover:shadow-sm"
      style={{
        background: value ? '#f8fafc' : '#ffffff',
        borderColor: value ? accent : border,
        borderWidth: value ? '1.5px' : '1px',
      }}
      data-testid={`pack-field-${field.id}`}
    >
      <Checkbox checked={value} onCheckedChange={onChange} className="mt-0.5" />
      <div className="flex-1 min-w-0">
        <LabelInline html={field.label} className="text-xs leading-snug block" style={{ color: '#0f172a', fontWeight: value ? 600 : 400 }} />
        {meta?.updated_at && value && (
          <div className="text-[9.5px] mt-0.5" style={{ color: '#94a3b8' }}>{formatMetaShort(meta)}</div>
        )}
      </div>
    </label>
  );
}


// "5m ago · Emmanuel" style compact stamp
function formatMetaShort(meta) {
  if (!meta?.updated_at) return '';
  const then = new Date(meta.updated_at);
  const diffMs = Date.now() - then.getTime();
  const min = Math.floor(diffMs / 60000);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  let rel;
  if (min < 1) rel = 'just now';
  else if (min < 60) rel = `${min}m ago`;
  else if (hr < 24) rel = `${hr}h ago`;
  else if (day < 30) rel = `${day}d ago`;
  else rel = then.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
  const who = meta.updated_by_name ? ` · ${meta.updated_by_name}` : '';
  return `${rel}${who}`;
}


function ChecklistGroup({ group, sectionId, naOn, onToggleNa, answers, answerMeta, setVal, liabilities = [], accent = '#4f46e5', border = '#c7d2fe' }) {
  // Expand each raw field into one-or-more "effective" fields. A field with
  // per_liability metadata fans out into ONE virtual field per matching
  // liability on the pack; if the client has no matching debt on file the
  // requirement is silently N/A (0 items rendered).
  const effectiveFields = React.useMemo(() => {
    const out = [];
    // Paid-out debts must NOT drive checklist personalisation — the broker
    // has flagged them as discharged, so no statement is required.
    const liveLiabilities = (liabilities || []).filter(l => !l.paid_out);
    group.fields.forEach(f => {
      const spec = f.per_liability;
      if (!spec) { out.push({ ...f, _origId: f.id }); return; }
      const types = Array.isArray(spec) ? spec : (spec.types || []);
      const template = Array.isArray(spec) ? null : (spec.label_template || null);
      const matches = liveLiabilities.filter(l => !types.length || types.includes(l.type));
      if (matches.length === 0) return; // no matching debts → silent N/A
      matches.forEach(l => {
        const baseLabel = (f.label || '').replace(/<[^>]+>/g, '');
        const personalisedLabel = template
          ? template
              .replace('{description}', l.description || l.type || 'Liability')
              .replace('{type}', l.type || '')
              .replace('{label}', baseLabel)
          : `${baseLabel} — ${l.description || l.type || 'Liability'}`;
        out.push({
          ...f,
          id: `${f.id}__lia_${l.id}`,
          _origId: f.id,
          _liability: l,
          label: personalisedLabel,
          per_liability: undefined, // consumed
        });
      });
    });
    return out;
  }, [group.fields, liabilities]);

  const mandatoryFields = effectiveFields.filter(f => f.mandatory);
  const optionalFields  = effectiveFields.filter(f => !f.mandatory);
  const allMandatoryDone = mandatoryFields.every(f => !!answers[f.id]);
  const anyOptionalDone  = optionalFields.some(f => !!answers[f.id]);
  const missingMandatory = mandatoryFields.filter(f => !answers[f.id]).length;

  // Hybrid ID-verification group: any_of=True with at least one mandatory
  // field → all mandatory MUST be ticked AND at least one optional too.
  const isHybrid = group.any_of && mandatoryFields.length > 0 && optionalFields.length > 0;
  const allDone = isHybrid
    ? (allMandatoryDone && anyOptionalDone)
    : group.any_of
      ? effectiveFields.some(f => !!answers[f.id])
      : allMandatoryDone;

  const totalDone = effectiveFields.filter(f => !!answers[f.id]).length;

  // Confirmation-prompt state — surfaced when the user checks a field that
  // carries `confirm_prompt` (e.g. the NextGen / lender ID-verification
  // question). The tick is only applied after the user confirms. Declared
  // ABOVE the early-return below so React hook order stays consistent.
  const [confirmField, setConfirmField] = useState(null);

  // If the group had per_liability fields that expanded to zero effective
  // items (no matching debts), and the whole group is now empty, hide it.
  if (effectiveFields.length === 0) return null;

  return (
    <div className="rounded-xl border overflow-hidden" data-testid={`pack-group-${sectionId}-${group.id}`}
      style={{ borderColor: naOn ? '#e2e8f0' : allDone ? '#a7f3d0' : border, background: '#ffffff' }}>
      <div className="flex items-center justify-between px-3 py-2.5 border-b"
        style={{
          background: naOn ? '#f8fafc' : allDone ? '#f0fdf4' : '#f8fafc',
          borderColor: naOn ? '#e2e8f0' : allDone ? '#a7f3d0' : '#e2e8f0',
        }}>
        <div className="flex items-center gap-2.5 min-w-0">
          {/* Big letter chip */}
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-[11px] font-black shrink-0"
            style={{ background: naOn ? '#94a3b8' : allDone ? '#10b981' : accent, color: '#ffffff' }}>
            {group.id}
          </span>
          <div className="min-w-0">
            <div className="text-[12.5px] font-black leading-tight" style={{ color: '#0f172a' }}>{group.name}</div>
            {group.fine && <div className="text-[10px] italic mt-0.5 leading-snug" style={{ color: '#64748b' }}>{group.fine}</div>}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 pl-2">
          {naOn ? (
            <span className="text-[10px] font-black uppercase tracking-wider" style={{ color: '#64748b' }}>Waived</span>
          ) : isHybrid ? (
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-black tabular-nums uppercase tracking-wider" style={{ color: allDone ? '#059669' : '#64748b' }}>
                {totalDone}/{effectiveFields.length}
              </span>
              {!allMandatoryDone && (
                <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: '#fee2e2', color: '#991b1b' }}>
                  {missingMandatory} req
                </span>
              )}
              {allMandatoryDone && !anyOptionalDone && optionalFields.length > 0 && (
                <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: '#fef3c7', color: '#92400e' }}>
                  pick 1 of {optionalFields.length}
                </span>
              )}
            </div>
          ) : group.any_of ? (
            <span className="text-[10px] font-black uppercase tracking-wider" style={{ color: allDone ? '#059669' : accent }}>
              {allDone ? `${totalDone} picked` : `pick one of ${effectiveFields.length}`}
            </span>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-black tabular-nums uppercase tracking-wider" style={{ color: missingMandatory ? '#dc2626' : allDone ? '#059669' : '#64748b' }}>
                {totalDone}/{effectiveFields.length}
              </span>
              {missingMandatory > 0 && (
                <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: '#fee2e2', color: '#991b1b' }}>
                  {missingMandatory} req
                </span>
              )}
            </div>
          )}
          {allDone && !naOn && <CheckCircle2 className="w-4 h-4" style={{ color: '#10b981' }} />}
          {group.na && (
            <label className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider cursor-pointer" style={{ color: '#64748b' }}>
              <Checkbox checked={naOn} onCheckedChange={onToggleNa} data-testid={`group-na-${sectionId}-${group.id}`} />
              N/A
            </label>
          )}
        </div>
      </div>
      {!naOn && (
        <div className="p-2.5 space-y-1">
          {effectiveFields.map(f => {
            const ticked = !!answers[f.id];
            const hasConfirm = !!f.confirm_prompt;
            // Highlight the NextGen mandatory question with a subtle amber
            // outline so it stands out inside the ID verification group.
            const highlight = hasConfirm && f.mandatory && !ticked;
            return (
              <label key={f.id} className="flex items-start gap-2 p-2 rounded-md cursor-pointer transition-colors"
                style={{
                  background: ticked ? '#f0fdf4' : (highlight ? '#fffbeb' : 'transparent'),
                  border: highlight ? '1px dashed #fcd34d' : '1px solid transparent',
                }}
                onMouseEnter={(e) => { if (!ticked && !highlight) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={(e) => { if (!ticked && !highlight) e.currentTarget.style.background = 'transparent'; }}
                data-testid={`pack-field-${f.id}`}>
                <Checkbox
                  checked={ticked}
                  onCheckedChange={(v) => {
                    // If the field carries a confirm_prompt AND the broker is
                    // trying to TICK it (not un-tick), pop a confirmation
                    // dialog and only commit the tick on explicit approval.
                    if (v && hasConfirm) {
                      setConfirmField(f);
                      return;
                    }
                    setVal(f.id, v);
                  }}
                  className="mt-0.5"
                  data-testid={`pack-field-${f.id}-checkbox`}
                />
                <div className="flex-1 min-w-0">
                  <LabelInline html={f.label} className="text-xs leading-snug block" style={{ color: '#0f172a', fontWeight: ticked ? 600 : 400 }} />
                  {answerMeta?.[f.id]?.updated_at && ticked && (
                    <div className="text-[9.5px] mt-0.5" style={{ color: '#94a3b8' }}>{formatMetaShort(answerMeta[f.id])}</div>
                  )}
                </div>
                {f.mandatory && !ticked && <span className="text-[9px] font-black px-1.5 py-0.5 rounded" style={{ background: '#fee2e2', color: '#991b1b' }}>REQ</span>}
              </label>
            );
          })}
        </div>
      )}

      {/* Confirmation dialog for fields with `confirm_prompt` (e.g. NextGen /
          lender ID verification). Only ticks the checkbox after the broker
          explicitly agrees they've double-checked the identity match. */}
      <AlertDialog open={!!confirmField} onOpenChange={(o) => { if (!o) setConfirmField(null); }}>
        <AlertDialogContent data-testid={`confirm-dialog-${confirmField?.id || 'none'}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm ID verification</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmField?.confirm_prompt || 'Are you sure you want to tick this item?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">No, go back</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmField) setVal(confirmField.id, true);
                setConfirmField(null);
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="confirm-yes"
            >
              Yes, I&rsquo;ve checked everything
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// LegacyPackEditor — the full pre-lodgement editor (deal summary hero,
// outstanding banner, SLA config, activity log, template sections).
// On post-lodgement it is collapsed by default under a "Pack contents"
// toggle since the Command Centre takes over the primary view.
// ─────────────────────────────────────────────────────────────────────

// ─── Waiting-on categorisation for pre-lodgement outstanding items ──
// Each outstanding item comes from a template section (notes, checklist,
// signoff, docs). We map section → "owner" so the broker can immediately
// see who's holding up the file. The mapping mirrors the language the
// user asked for: Waiting on Broker / Waiting on Client / Credit Team /
// Support Team.
const OUTSTANDING_OWNER = {
  notes:      { key: 'credit',       label: 'Credit Team Action Required',  subtitle: 'Credit team to write these loan-submission notes', tone: '#7c3aed', ink: '#4c1d95', bg: '#f5f3ff', border: '#c4b5fd', dot: '#8b5cf6' },
  signoff:    { key: 'broker',       label: 'Waiting on Broker sign-off',   subtitle: 'Broker to sign off before lodgement',    tone: '#4f46e5', ink: '#312e81', bg: '#eef2ff',  border: '#c7d2fe', dot: '#6366f1' },
  documents:  { key: 'client',       label: 'Waiting on Client documents',  subtitle: 'Chase the client for outstanding docs',  tone: '#d97706', ink: '#78350f', bg: '#fffbeb',  border: '#fcd34d', dot: '#f59e0b' },
  checklist:  { key: 'credit',       label: 'Credit Team Action Required',  subtitle: 'Policy checklist items to confirm',       tone: '#7c3aed', ink: '#4c1d95', bg: '#f5f3ff',  border: '#c4b5fd', dot: '#8b5cf6' },
  qc:         { key: 'support',      label: 'Support Team Action Required', subtitle: 'Internal QC steps to complete',           tone: '#0d9488', ink: '#134e4a', bg: '#f0fdfa',  border: '#5eead4', dot: '#14b8a6' },
};
const OWNER_FALLBACK = { key: 'broker', label: 'Waiting on Broker', subtitle: 'Broker to complete these items', tone: '#475569', ink: '#0f172a', bg: '#f8fafc', border: '#e2e8f0', dot: '#94a3b8' };

// Human-readable current-status banner. Uses pack.status + completion to
// derive one clear "Currently waiting on X" line at the top of the editor.
function currentWaitingState(pack) {
  if (pack.completion >= 100) {
    return { tone: '#059669', bg: '#ecfdf5', border: '#6ee7b7', ink: '#064e3b', label: 'Ready for Lodgement', detail: 'All mandatory items complete — broker to press Lodge with Lender.', icon: CheckCircle2 };
  }
  const s = pack.status;
  if (s === 'waiting_on_info') {
    return { tone: '#d97706', bg: '#fffbeb', border: '#fcd34d', ink: '#78350f', label: 'Waiting on Client', detail: 'Chase the client for the outstanding documents / information.', icon: AlertCircle };
  }
  if (s === 'ready_for_review') {
    return { tone: '#7c3aed', bg: '#f5f3ff', border: '#c4b5fd', ink: '#4c1d95', label: 'Credit Team Action Required', detail: 'File is ready for credit team review — no delay on broker side.', icon: AlertCircle };
  }
  if (s === 'changes_required') {
    return { tone: '#e11d48', bg: '#fff1f2', border: '#fda4af', ink: '#881337', label: 'Support Team Action Required', detail: 'Support / credit has flagged changes — broker to update the file.', icon: AlertCircle };
  }
  return { tone: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', ink: '#1e3a8a', label: 'Waiting on Broker', detail: 'Broker to complete the outstanding items below to progress the file.', icon: AlertCircle };
}

function OutstandingBanner({ pack }) {
  const groups = React.useMemo(() => {
    const g = new Map();
    (pack.outstanding || []).forEach((o, idx) => {
      const owner = OUTSTANDING_OWNER[o.section_id] || OWNER_FALLBACK;
      const bucket = g.get(owner.key) || { owner, items: [] };
      bucket.items.push({ ...o, _idx: idx });
      g.set(owner.key, bucket);
    });
    // Stable order: broker → client → credit → support → fallback
    const order = ['broker', 'client', 'credit', 'support'];
    return order.map(k => g.get(k)).filter(Boolean);
  }, [pack.outstanding]);

  const waitingState = currentWaitingState(pack);
  const WaitingIcon = waitingState.icon;

  return (
    <div className="rounded-2xl border-2 shadow-sm overflow-hidden" data-testid="pack-outstanding-banner"
      style={{ borderColor: '#fcd34d', background: '#ffffff', colorScheme: 'light' }}>
      {/* Top strip — the ONE thing every user needs to see: who's holding this up */}
      <div className="px-5 py-3 flex items-center justify-between gap-3 border-b"
        style={{ background: waitingState.bg, borderColor: waitingState.border }} data-testid="pack-outstanding-status">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: waitingState.tone }}>
            <WaitingIcon className="w-4.5 h-4.5" style={{ color: '#ffffff' }} />
          </div>
          <div className="min-w-0">
            <div className="text-[9.5px] font-black uppercase tracking-[0.16em]" style={{ color: waitingState.tone }}>Currently</div>
            <div className="text-base font-black leading-tight truncate" style={{ color: waitingState.ink }}>{waitingState.label}</div>
            <div className="text-[11px] font-semibold mt-0.5" style={{ color: waitingState.ink, opacity: 0.85 }}>{waitingState.detail}</div>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[9px] font-black uppercase tracking-widest" style={{ color: waitingState.ink, opacity: 0.75 }}>Completion</div>
          <div className="text-2xl font-black tabular-nums leading-none" style={{ color: waitingState.ink }}>{pack.completion}%</div>
          <div className="text-[10px] font-bold" style={{ color: waitingState.ink, opacity: 0.75 }}>
            {pack.outstanding.length} item{pack.outstanding.length === 1 ? '' : 's'} outstanding
          </div>
        </div>
      </div>

      {/* Grouped outstanding items — each group shows who's responsible + what's needed + how long */}
      <div className="p-3 space-y-2.5" style={{ background: '#fffbeb' }}>
        {groups.map((g) => (
          <OutstandingGroup key={g.owner.key} group={g} pack={pack} />
        ))}
      </div>
    </div>
  );
}

function OutstandingGroup({ group, pack }) {
  const [expanded, setExpanded] = useState(true);
  const { owner, items } = group;
  // Rough "age" of the block: how long since the pack was last touched — a
  // proxy since we don't track per-field first-open timestamps.
  const ageDays = pack.created_at ? Math.max(0, Math.floor((Date.now() - new Date(pack.created_at).getTime()) / (1000 * 60 * 60 * 24))) : null;
  return (
    <div className="rounded-lg border overflow-hidden" style={{ borderColor: owner.border, background: '#ffffff' }} data-testid={`outstanding-group-${owner.key}`}>
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-3 py-2 hover:opacity-95"
        style={{ background: owner.bg }}
        data-testid={`outstanding-group-toggle-${owner.key}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-md text-[11px] font-black tabular-nums shrink-0" style={{ background: owner.tone, color: '#ffffff' }}>
            {items.length}
          </span>
          <div className="min-w-0 text-left">
            <div className="text-[12.5px] font-black truncate" style={{ color: owner.ink }}>{owner.label}</div>
            <div className="text-[10.5px] font-semibold truncate" style={{ color: owner.ink, opacity: 0.8 }}>{owner.subtitle}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {ageDays !== null && (
            <span className="text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: '#ffffff', color: owner.ink, border: `1px solid ${owner.border}` }}>
              {ageDays}d since created
            </span>
          )}
          {expanded ? <ChevronUp className="w-3.5 h-3.5" style={{ color: owner.tone }} /> : <ChevronDown className="w-3.5 h-3.5" style={{ color: owner.tone }} />}
        </div>
      </button>
      {expanded && (
        <ul className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
          {items.map((o) => (
            <li key={o._idx} className="flex items-start gap-2 text-[12px]" data-testid={`outstanding-item-${o._idx}`}>
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: owner.dot }} />
              <span className="leading-snug" style={{ color: '#0f172a' }} title={o.label}>{o.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// LegacyPackEditor — the full pre-lodgement editor
function LegacyPackEditor({ pack, template, queueChange, val, setVal, setNa, setSla, showActivity, setShowActivity, isDone, onPackChange }) {
  const { token } = useAuth();
  const auth = React.useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  return (
    <div className="space-y-6">
          <div className="rounded-2xl border border-slate-200 shadow-sm overflow-hidden bg-white" data-testid="pack-deal-summary" style={{ colorScheme: 'light' }}>
            {/* Top row: client + lender chip + completion */}
            <div className="relative px-6 pt-5 pb-5 bg-gradient-to-br from-white via-white to-blue-50">
              {/* Accent bar */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-500" />

              <div className="flex items-start justify-between gap-6 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-black uppercase tracking-[0.2em]" style={{ color: '#64748b' }}>Deal summary</div>
                  <h2 className="mt-1 text-2xl font-black tracking-tight truncate" style={{ color: '#0f172a' }}>
                    {pack.client_name}
                  </h2>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-100 border border-blue-200" style={{ color: '#1e40af' }}>
                      <Landmark className="w-3 h-3" style={{ color: '#1e40af' }} />
                      <span className="text-xs font-bold">{pack.lender}</span>
                    </span>
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 border border-slate-200" style={{ color: '#0f172a' }}>
                      <span className="text-[10px] font-black uppercase tracking-wider" style={{ color: '#64748b' }}>Broker</span>
                      <span className="text-xs font-bold">{pack.broker}</span>
                    </span>
                    {pack.credit_team_member ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-50 border border-indigo-200" style={{ color: '#3730a3' }}>
                        <span className="text-[10px] font-black uppercase tracking-wider" style={{ color: '#6366f1' }}>Credit</span>
                        <span className="text-xs font-bold">{pack.credit_team_member}</span>
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="shrink-0 text-right min-w-[180px]">
                  <div className="text-[10px] font-black uppercase tracking-[0.2em]" style={{ color: '#64748b' }}>Completion</div>
                  <div className="mt-1 flex items-baseline justify-end gap-1">
                    <span className="text-5xl font-black tabular-nums leading-none tracking-tight bg-gradient-to-br from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                      {pack.completion}
                    </span>
                    <span className="text-2xl font-black" style={{ color: '#94a3b8' }}>%</span>
                  </div>
                  <div className="mt-2 h-2 rounded-full overflow-hidden ml-auto max-w-[180px]" style={{ background: '#e2e8f0' }}>
                    <div className="h-full rounded-full transition-all bg-gradient-to-r from-emerald-400 to-emerald-500"
                      style={{ width: `${pack.completion || 0}%` }} />
                  </div>
                  <div className="mt-2 text-[10.5px] font-semibold" style={{ color: '#64748b' }}>
                    {(pack.outstanding || []).length === 0
                      ? 'Ready to proceed'
                      : `${pack.outstanding.length} item${pack.outstanding.length === 1 ? '' : 's'} outstanding`}
                  </div>
                </div>
              </div>
            </div>

            {/* Editable meta strip */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-6 py-4 border-t" style={{ background: '#f8fafc', borderColor: '#e2e8f0' }}>
              <div>
                <label className="text-[9.5px] font-black uppercase tracking-widest mb-1 block" style={{ color: '#64748b' }}>Credit officer</label>
                <Input value={pack.credit_team_member || ''}
                  onChange={e => queueChange({ credit_team_member: e.target.value })}
                  placeholder="Assign…"
                  className="h-8 text-xs bg-white"
                  style={{ color: '#0f172a' }}
                  data-testid="pack-credit-officer" />
              </div>
              <div>
                <label className="text-[9.5px] font-black uppercase tracking-widest mb-1 block" style={{ color: '#64748b' }}>Status</label>
                <Select value={pack.status} onValueChange={v => queueChange({ status: v })}>
                  <SelectTrigger data-testid="pack-status-select" className="h-8 text-xs bg-white" style={{ color: '#0f172a' }}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABELS).map(([k, l]) => (
                      <SelectItem key={k} value={k}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Timestamps */}
            <div className="px-6 py-2.5 border-t flex flex-wrap items-center gap-x-5 gap-y-1 text-[10.5px] bg-white" style={{ color: '#64748b', borderColor: '#e2e8f0' }}>
              <span><b style={{ color: '#334155' }}>Created</b> {new Date(pack.created_at).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })} by {pack.created_by_name}</span>
              <span style={{ color: '#cbd5e1' }}>•</span>
              <span><b style={{ color: '#334155' }}>Last updated</b> {new Date(pack.last_updated_at).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })} by {pack.last_updated_by_name}</span>
              {pack.completed_at && (
                <>
                  <span style={{ color: '#cbd5e1' }}>•</span>
                  <span style={{ color: '#047857' }}><b>Completed</b> {new Date(pack.completed_at).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                </>
              )}
            </div>
          </div>

          {/* ── Client Liabilities (Step 2) — powers checklist personalisation ── */}
          <LiabilitiesSection
            pack={pack}
            onPackChange={onPackChange}
            savePack={async (patch) => {
              const res = await fetch(`${backendUrl}/api/lender-packs/${pack.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', ...auth },
                body: JSON.stringify(patch),
              });
              if (!res.ok) throw new Error(await res.text());
              return res.json();
            }}
            disabled={isDone}
          />

          {/* ── Missing / Outstanding banner ────────────────── */}
          {(pack.outstanding || []).length > 0 ? (
            <OutstandingBanner pack={pack} />
          ) : (
            <div className="rounded-2xl border-2 shadow-sm px-5 py-4 flex items-center gap-3" data-testid="pack-ready-banner"
              style={{ borderColor: '#6ee7b7', background: '#ecfdf5', colorScheme: 'light' }}>
              <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: '#10b981' }}>
                <CheckCircle2 className="w-5 h-5" style={{ color: '#ffffff' }} />
              </div>
              <div>
                <div className="text-sm font-black" style={{ color: '#064e3b' }}>Ready for Lodgement — all mandatory items complete.</div>
                <div className="text-[11.5px]" style={{ color: '#047857' }}>
                  {isDone ? 'This pack has been marked completed.' : 'Click Lodge with Lender in the toolbar to hand it off.'}
                </div>
              </div>
            </div>
          )}

          {/* ── Lender SLA card (visible always; wired to autosave) ── */}
          <div className="rounded-2xl border-2 shadow-sm overflow-hidden bg-white" data-testid="pack-sla-card" style={{ borderColor: '#e2e8f0', colorScheme: 'light' }}>
            <div className="px-5 py-3 flex items-center justify-between border-b" style={{ background: '#f8fafc', borderColor: '#e2e8f0' }}>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: '#0f172a' }}>
                  <Timer className="w-4 h-4" style={{ color: '#fff' }} />
                </div>
                <div>
                  <div className="text-sm font-black" style={{ color: '#0f172a' }}>Lender SLA</div>
                  <div className="text-[11px]" style={{ color: '#64748b' }}>Business days for each stage — weekends + AU public holidays are skipped when calculating expected response.</div>
                </div>
              </div>
              {pack.expected_response_date && (
                <div className="text-right">
                  <div className="text-[9.5px] font-black uppercase tracking-widest" style={{ color: '#64748b' }}>Expected response · {pack.sla_stage?.replace(/_/g, ' ')}</div>
                  <div className="text-sm font-black" style={{ color: '#0f172a' }}>{new Date(pack.expected_response_date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}</div>
                  {pack.days_remaining !== null && (
                    <div className="text-[10.5px] font-bold" style={{
                      color: pack.sla_indicator === 'overdue' ? '#b91c1c'
                        : pack.sla_indicator === 'due_today' ? '#b45309'
                        : '#047857'
                    }}>
                      {pack.days_remaining > 0 && `${pack.days_remaining} BD remaining`}
                      {pack.days_remaining === 0 && 'Due today'}
                      {pack.days_remaining < 0 && `${Math.abs(pack.days_remaining)} BD overdue`}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 p-4">
              {SLA_META.map(([key, label]) => (
                <div key={key}>
                  <label className="text-[9.5px] font-black uppercase tracking-widest mb-1 block leading-tight min-h-[24px]" style={{ color: '#64748b' }}>{label}</label>
                  <div className="relative">
                    <Input
                      type="number" min="0" step="1"
                      value={(pack.sla || {})[key] ?? 5}
                      onChange={e => setSla(key, e.target.value)}
                      className="h-8 text-xs pr-8"
                      style={{ color: '#0f172a' }}
                      data-testid={`sla-${key}`}
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold" style={{ color: '#94a3b8' }}>BD</span>
                  </div>
                </div>
              ))}
            </div>
          </div>


          {/* ── Activity log (collapsible) ──────────────────── */}
          <div className="rounded-2xl border shadow-sm overflow-hidden bg-white" data-testid="pack-activity-log" style={{ borderColor: '#e2e8f0', colorScheme: 'light' }}>
            <button onClick={() => setShowActivity(v => !v)}
              className="w-full px-5 py-3 flex items-center justify-between hover:bg-slate-50"
              style={{ background: '#f8fafc' }}
              data-testid="pack-activity-toggle">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: '#6366f1' }}>
                  <History className="w-4 h-4" style={{ color: '#fff' }} />
                </div>
                <div className="text-left">
                  <div className="text-sm font-black" style={{ color: '#0f172a' }}>Activity history</div>
                  <div className="text-[11px]" style={{ color: '#64748b' }}>{(pack.activity || []).length} entries · who changed what, when</div>
                </div>
              </div>
              {showActivity ? <ChevronUp className="w-4 h-4" style={{ color: '#64748b' }} /> : <ChevronDown className="w-4 h-4" style={{ color: '#64748b' }} />}
            </button>
            {showActivity && (
              <ul className="max-h-[360px] overflow-y-auto divide-y" style={{ borderColor: '#e2e8f0' }}>
                {(pack.activity || []).slice().reverse().map((a, idx) => (
                  <li key={idx} className="px-5 py-2.5 text-[11.5px]" style={{ color: '#0f172a' }}>
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="font-semibold">{a.details}</span>
                      <span className="text-[10px] font-bold tabular-nums shrink-0" style={{ color: '#94a3b8' }}>{new Date(a.ts).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' })}</span>
                    </div>
                    <div className="text-[10.5px]" style={{ color: '#64748b' }}>
                      by <b style={{ color: '#334155' }}>{a.user}</b> · {a.action.replace(/_/g, ' ')}
                      {a.before !== undefined && a.after !== undefined && (
                        <span className="ml-1 italic">
                          — was <code className="text-[10px] font-mono" style={{ color: '#b45309' }}>{JSON.stringify(a.before)}</code>, now <code className="text-[10px] font-mono" style={{ color: '#047857' }}>{JSON.stringify(a.after)}</code>
                        </span>
                      )}
                    </div>
                  </li>
                ))}
                {(pack.activity || []).length === 0 && (
                  <li className="px-5 py-4 text-center text-[11px] italic" style={{ color: '#94a3b8' }}>No activity yet.</li>
                )}
              </ul>
            )}
          </div>

          {/* Sections — each one gets its own distinctive card, so give them extra breathing room */}
          <div className="pt-3 space-y-8">
            {template.sections
              .filter(section => section.kind !== 'signoff')
              .map(section => (
                <Section key={section.id} section={section} pack={pack}
                  val={val} setVal={setVal} setNa={setNa} onPackChange={onPackChange} />
              ))}
          </div>
    </div>
  );
}
